/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faCircleInfo, faEye, faPlus } from '@fortawesome/free-solid-svg-icons';
import { getIn } from 'formik';
import XInput from '../../../component/form/XInput';
import XSelect from '../../../component/form/XSelect';
import useLocationData from '../../../hooks/useLocationData';
import useAxios from '../../../hooks/useAxios';
import { toaster } from '../../../utils/toast';
import { Location } from '../../../utils/interface';
import { timezoneOptions } from '../../../utils/options';
import PhoneNumberInput from '../../../component/phoneInput/phoneInput';

const FieldInfo = ({ id, text }: { id: string; text: string }) => (
    <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={id} className="text-start">{text}</Tooltip>}
    >
        <span className="ms-2 cursor-pointer">
            <FontAwesomeIcon icon={faCircleInfo} />
        </span>
    </OverlayTrigger>
);

// Must live at module level. Defining it inside LocationCard remounts the inputs on every keystroke.
const Restricted = ({ locked, children }: { locked: boolean; children: React.ReactNode }) => (
    <fieldset disabled={locked} className="border-0 p-0 m-0" style={{ minWidth: 0 }}>
        {children}
    </fieldset>
);

const MAX_LOGO_SIZE_BYTES = 1024 * 1024;
const LOCKED_TEXT = 'Only Account Admin and above can change this field.';
const RSS_LOCKED_TEXT = 'Only Location Admin and above can change RSS feeds.';
const LOGO_LOCKED_TEXT = 'Only Location Admin and above can change the location logo.';

interface LocationCardProps {
    index: number;
    formik: any;
    locationData: Location;
    removeLocationHandler: (index: number) => void;
    canManageLocations: boolean; // Org / Account Admin: all location fields
    canEditRss: boolean;         // Org / Account Admin / Location Admin: RSS feeds + logo
}

const LocationCard: React.FC<LocationCardProps> = ({
    index,
    formik,
    removeLocationHandler,
    locationData,
    canManageLocations,
    canEditRss,
}) => {
    const { countries, states, fetchStates } = useLocationData();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const locationFieldPrefix = `locations[${index}]`;
    const currentLogoUrl = getIn(formik.values, `${locationFieldPrefix}.logoUrl`) || locationData?.logoUrl;
    const currentLogoFile = getIn(formik.values, `${locationFieldPrefix}.logoFile`);
    const isRemoveLogo = getIn(formik.values, `${locationFieldPrefix}.removeLogo`);
    const [previewLogo, setPreviewLogo] = useState<string | null>(null);

    // Plain function returning a node (not a component), so it is safe to define here
    const label = (text: string, key: string): any =>
        canManageLocations
            ? text
            : <>{text}<FieldInfo id={`${key}-lock-${index}`} text={LOCKED_TEXT} /></>;

    useEffect(() => {
        if (currentLogoFile instanceof File) {
            setPreviewLogo(URL.createObjectURL(currentLogoFile));
        } else if (!isRemoveLogo && currentLogoUrl) {
            setPreviewLogo(currentLogoUrl);
        } else {
            setPreviewLogo(null);
        }
    }, [currentLogoFile, currentLogoUrl, isRemoveLogo]);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > MAX_LOGO_SIZE_BYTES) {
            toaster({ message: 'Location logo exceeds 1 MB. Please choose a location logo no larger than 1 MB.', success: false });
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        if (!file.type.startsWith('image/')) {
            toaster({ message: 'File is not an image. Please upload an image file.', success: false });
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        setPreviewLogo(objectUrl);
        formik.setFieldValue(`${locationFieldPrefix}.logoFile`, file);
        formik.setFieldValue(`${locationFieldPrefix}.removeLogo`, false);
    };

    const handleRemoveLogo = () => {
        setPreviewLogo(null);
        formik.setFieldValue(`${locationFieldPrefix}.logoFile`, null);
        formik.setFieldValue(`${locationFieldPrefix}.logoUrl`, null);
        formik.setFieldValue(`${locationFieldPrefix}.logo`, null);
        formik.setFieldValue(`${locationFieldPrefix}.removeLogo`, true);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const rssFeeds = getIn(formik.values, `${locationFieldPrefix}.rss_feeds`) || [];
    const openRssFeed = (rssFeedUrl: string) => {
        if (!rssFeedUrl) return;
        try {
            const parsed = new URL(rssFeedUrl);
            if (!['http:', 'https:'].includes(parsed.protocol)) return;
            window.open(parsed.href, '_blank', 'noopener,noreferrer');
        } catch {
            return;
        }
    };

    useEffect(() => {
        if (locationData?.country_id) {
            fetchStates(locationData?.country_id)
        }
    }, [locationData?.country_id])

    const isActive = getIn(formik.values, `${locationFieldPrefix}.is_active`) ?? true;

    return (
        <div className="add-location-box">
            <Row>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XInput
                            name={`${locationFieldPrefix}.display_name`}
                            label={label('Location Name', 'name')}
                            placeholder="e.g. Nashville"
                            formik={formik}
                        />
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XInput name={`${locationFieldPrefix}.address`} label={label('Address', 'address')} placeholder="Enter your address" formik={formik}/>
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XSelect
                            name={`${locationFieldPrefix}.country_id`}
                            label={label('Country', 'country')}
                            options={Object.values(countries)}
                            formik={formik}
                            onChange={(e)=>{
                                formik.handleChange(e)
                                formik.setFieldValue(`${locationFieldPrefix}.state_id`, '');
                            }}
                        />
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XSelect
                            name={`${locationFieldPrefix}.state_id`}
                            label={label('State', 'state')}
                            options={Object.values(states)}
                            formik={formik}
                        />
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XInput
                            name={`${locationFieldPrefix}.city`}
                            label={label('City', 'city')}
                            placeholder="City"
                            formik={formik}
                        />
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XInput name={`${locationFieldPrefix}.zipcode`} label={label('ZIP Code', "zip")} placeholder="eg: 12345" formik={formik}/>
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <PhoneNumberInput
                            value={getIn(formik.values, `${locationFieldPrefix}.phone`) || ''}
                            onChange={(phone) => formik.setFieldValue(`${locationFieldPrefix}.phone`, phone)}
                            error={getIn(formik.errors, `${locationFieldPrefix}.phone`)}
                            touched={getIn(formik.touched, `${locationFieldPrefix}.phone`)}
                            label="Phone"
                            placeholder="Enter phone number"
                            country="us"
                        />
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XInput
                            name={`${locationFieldPrefix}.reply_to_email`}
                            label={label('Reply-to Email', 'reply')}
                            placeholder="contact@your-brand-domain.com"
                            formik={formik}
                        />
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XInput
                            name={`${locationFieldPrefix}.from_name`}
                            label={label('From Email Name', 'from')}
                            placeholder="Your Brand Name"
                            formik={formik}
                        />
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canManageLocations}>
                        <XSelect
                            name={`${locationFieldPrefix}.location_timezone`}
                            label={label('Timezone', 'tz')}
                            options={timezoneOptions}
                            formik={formik}
                        />
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12}>
                    <Restricted locked={!canEditRss}>
                        <Form.Group className="form-group position-relative">
                            <Form.Label>
                                Location Logo
                                <FieldInfo
                                    id={`loc-logo-info-${index}`}
                                    text={canEditRss? "Optional custom logo for this location. If not uploaded, the organization's logo is used in emails as fallback.": LOGO_LOCKED_TEXT}
                                />
                            </Form.Label>
                            <div className="position-relative">
                                <Form.Control
                                    ref={fileInputRef}
                                    type="file"
                                    id={`location-logo-file-${index}`}
                                    accept="image/*"
                                    className="visually-hidden"
                                    onChange={handleLogoChange}
                                />
                                <label htmlFor={`location-logo-file-${index}`} className="logo-dropzone">
                                    <svg className="logo-dropzone-icon" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14.5v3A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-3" />
                                    </svg>
                                    <span className="logo-dropzone-action">{previewLogo ? 'Edit Logo' : 'Upload logo'}</span>
                                    {previewLogo && (
                                        <img className="logo-dropzone-preview" alt="Location logo preview" src={previewLogo} />
                                    )}
                                </label>
                                {previewLogo && canEditRss && (
                                    <button
                                        type="button"
                                        className="logo-dropzone-remove"
                                        aria-label="Remove location logo"
                                        title="Remove Logo"
                                        onClick={handleRemoveLogo}
                                    >
                                        <FontAwesomeIcon icon={faCircleXmark} />
                                    </button>
                                )}
                            </div>
                        </Form.Group>
                    </Restricted>
                </Col>
                <Col xl={4} md={6} xs={12} className="d-flex align-items-center">
                    <div className="d-flex flex-column">
                        <Form.Check
                            type="switch"
                            id={`${locationFieldPrefix}-is-active`}
                            disabled={!canManageLocations}
                            label={
                                <span>
                                    Location Status:{" "}
                                    <strong className={isActive ? "text-success" : "text-danger"}>
                                        {isActive ? "Active" : "Deactivated"}
                                    </strong>
                                    {!canManageLocations && <FieldInfo id={`status-lock-${index}`} text={LOCKED_TEXT} />}
                                </span>
                            }
                            checked={isActive}
                            onChange={(e) =>
                                formik.setFieldValue(`${locationFieldPrefix}.is_active`, e.target.checked)
                            }
                        />
                        {!isActive && (
                            <small className="text-danger mt-1">
                                Deactivating will pause active journeys and block new campaign scheduling for this location.
                            </small>
                        )}
                    </div>
                </Col>
                <Col xl={12} md={12} xs={12}>
                    <Form.Group className="form-group">
                        <div className="rss-section-header">
                            <div className="d-flex align-items-center justify-content-between">
                                <h6 className="rss-section-title mb-0">
                                    RSS Feeds
                                    <FieldInfo
                                        id={`rss-url-info-${index}`}
                                        text={canEditRss?"Add the RSS feeds available for this location. Email templates select one feed when they are created.": RSS_LOCKED_TEXT}
                                    />
                                </h6>
                                {canEditRss && (
                                    <span className="add-link">
                                        <span className='btn-add btn-add-sm' onClick={() => {
                                                formik.setFieldValue(`${locationFieldPrefix}.rss_feeds`, [...rssFeeds, { feed_name: '', feed_url: '', max_items: '' }]);
                                            }}>
                                            <FontAwesomeIcon icon={faPlus} /> Add
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>
                        {rssFeeds.map((feed: any, feedIndex: number) => {
                            const feedPrefix = `${locationFieldPrefix}.rss_feeds[${feedIndex}]`;
                            const nameName = `${feedPrefix}.feed_name`;
                            const urlName = `${feedPrefix}.feed_url`;
                            const maxName = `${feedPrefix}.max_items`;
                            return (<Row className="mb-3" key={feed.id || feedIndex}>
                                    <Col xl={4} md={6} xs={12}>
                                        <Form.Label>RSS Feed Name</Form.Label>
                                        <Form.Control name={nameName} placeholder="e.g. Newsletters" disabled={!canEditRss}{...formik.getFieldProps(nameName)} isInvalid={getIn(formik.touched, nameName) && !!getIn(formik.errors, nameName)} />
                                        <Form.Control.Feedback type="invalid">{getIn(formik.errors, nameName)}</Form.Control.Feedback>
                                    </Col>
                                    <Col xl={4} md={6} xs={12}>
                                        <Form.Label>RSS Feed URL</Form.Label>
                                        <div className="position-relative">
                                            <Form.Control name={urlName} placeholder="https://example.com/feed" className="pe-5" disabled={!canEditRss} {...formik.getFieldProps(urlName)} isInvalid={getIn(formik.touched, urlName) && !!getIn(formik.errors, urlName)} />
                                            <span className="position-absolute end-0 translate-middle-y me-3" style={{ top: '50%', cursor: feed.feed_url ? 'pointer' : 'not-allowed' }} onClick={() => openRssFeed(feed.feed_url)}><FontAwesomeIcon icon={faEye} /></span>
                                            <Form.Control.Feedback type="invalid">{getIn(formik.errors, urlName)}</Form.Control.Feedback>
                                        </div>
                                    </Col>
                                    <Col xl={4} md={6} xs={12}>
                                        <Form.Label>
                                            Max Items
                                            <FieldInfo
                                                id={`rss-max-info-${index}-${feedIndex}`}
                                                text="Limit on the number of RSS items included per email send (default is 3, maximum 10)."
                                            />
                                        </Form.Label>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="flex-grow-1">
                                                <Form.Control name={maxName} type="number" min="1" max="10" placeholder="Default: 3 (Max: 10)" disabled={!canEditRss} {...formik.getFieldProps(maxName)} isInvalid={getIn(formik.touched, maxName) && !!getIn(formik.errors, maxName)} onKeyDown={(e: any) => { if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault(); }} />
                                                <Form.Control.Feedback type="invalid">{getIn(formik.errors, maxName)}</Form.Control.Feedback>
                                            </div>
                                            {canEditRss && (
                                                <Button variant="link" className="text-danger p-0 flex-shrink-0" onClick={() => formik.setFieldValue(`${locationFieldPrefix}.rss_feeds`, rssFeeds.filter((_: any, i: number) => i !== feedIndex))}><FontAwesomeIcon icon={faCircleXmark} size="lg" /></Button>)}
                                        </div>
                                    </Col>
                                </Row>
                            );
                        })}
                    </Form.Group>
                </Col>
            </Row>
            {canManageLocations && (
                <div className="d-flex justify-content-end">
                    {formik.values.locations.length > 1 && (
                        <Button
                            variant="primary"
                            className="btn-theme btn-red d-flex align-items-center"
                            onClick={() => removeLocationHandler(index)}
                        >
                            Delete <FontAwesomeIcon icon={faCircleXmark} />
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default LocationCard;
